import { useQuery } from '@tanstack/react-query';
import PaymentsAPIServiceV2 from './PaymentsServiceV2';
import { EmployeeDetailRequest } from './ReportsInternalTypes';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const REPORTS_QUERY_KEYS = {
    GET_EMPLOYEE_DETAILS: 'GET_EMPLOYEE_DETAILS',
    GET_EMPLOYEE_SICKNESS: 'GET_EMPLOYEE_SICKNESS',
    GET_DEPARTMENTS: 'GET_DEPARTMENTS',
    GET_USER_COMMUNICATION_HISTORY: 'GET_USER_COMMUNICATION_HISTORY',
    GET_CAMPAIGN_ACTIVATION_FUNNEL: 'GET_CAMPAIGN_ACTIVATION_FUNNEL',
    GET_CAMPAIGN_FUNNEL_CAMPAIGNS: 'GET_CAMPAIGN_FUNNEL_CAMPAIGNS'
};

/**
 * Per-user communication & activity history report (US-3250, US-3385). SuperAdmin-only on
 * the backend (403 otherwise). companyId + identifier (a GUID or an email) are both required
 * so a user can never be pulled outside their company; the backend resolves the identifier to
 * a single user within that company. On a non-200 the thrown axios error carries the stable
 * error envelope ({ code, candidates? }) which the page reads via `error`; retry is disabled
 * so a 400/404/409 surfaces immediately instead of being retried.
 */
export const useUserCommunicationHistoryQuery = (
    companyId: string,
    identifier: string,
    enabled: boolean
) =>
    useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_USER_COMMUNICATION_HISTORY,
            companyId,
            identifier
        ],
        queryFn: () =>
            paymentsService
                .getUserCommunicationHistory(companyId, identifier)
                .then((res) => res.data),
        enabled,
        retry: false
    });

/**
 * Aggregate per-campaign activation funnel report (US-3251). SuperAdmin-only on the
 * backend (401/403 otherwise).
 *
 * `companyId` undefined is a distinct, valid scope — the cross-company aggregate — so it
 * must stay in the query key: swapping "Any" for a company has to be a cache miss.
 */
export const useCampaignActivationFunnelQuery = (
    campaignId: string,
    companyId: string | undefined,
    attributionWindowDays: number,
    enabled: boolean
) =>
    useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_CAMPAIGN_ACTIVATION_FUNNEL,
            campaignId,
            companyId ?? null,
            attributionWindowDays
        ],
        queryFn: () =>
            paymentsService
                .getCampaignActivationFunnel(
                    campaignId,
                    companyId,
                    attributionWindowDays
                )
                .then((res) => res.data),
        enabled,
        retry: false
    });

/**
 * Campaigns available to the funnel selector (US-3372). SuperAdmin-only. The backend
 * returns them ascending by numeric campaign id — callers must not re-sort, since that
 * order is the required one and is not the alphabetic order a client sort would produce.
 */
export const useCampaignFunnelCampaignsQuery = (enabled: boolean) =>
    useQuery({
        queryKey: [REPORTS_QUERY_KEYS.GET_CAMPAIGN_FUNNEL_CAMPAIGNS],
        queryFn: () =>
            paymentsService
                .getCampaignFunnelCampaigns()
                .then((res) => res.data),
        enabled,
        retry: false
    });

export const useReportsQuery = ({
    enableGetEmployeeSicknessQuery = false,
    enableGetDepartmentsQuery = false,
    employeeDetailParams = undefined
}: {
    enableGetEmployeeDetailsQuery?: boolean;
    enableGetEmployeeSicknessQuery?: boolean;
    enableGetDepartmentsQuery?: boolean;
    employeeDetailParams?: EmployeeDetailRequest;
}) => {
    const getEmployeeSicknessQuery = useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_EMPLOYEE_SICKNESS,
            employeeDetailParams
        ],
        queryFn: () =>
            paymentsService.getEmployeeSickness(
                employeeDetailParams!.departments
            ),
        enabled: enableGetEmployeeSicknessQuery,
        retry: false
    });

    const getDepartmentsQuery = useQuery({
        queryKey: [REPORTS_QUERY_KEYS.GET_DEPARTMENTS],
        queryFn: () => paymentsService.getDepartments(),
        enabled: enableGetDepartmentsQuery,
        retry: false
    });

    return {
        getEmployeeSicknessQuery,
        getDepartmentsQuery
    };
};

export const useEmployeeDetailsQuery = (
    enabled: boolean,
    employeeDetailParams: EmployeeDetailRequest
) =>
    useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_EMPLOYEE_DETAILS,
            employeeDetailParams.departments,
            employeeDetailParams.startDate,
            employeeDetailParams.endDate
        ],
        queryFn: () =>
            paymentsService.getEmployeeDetails(employeeDetailParams!),
        enabled: enabled,
        retry: false
    });
